"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function FossilStreamlinedPurchasing({ section }) {
  return (
    <section className="py-20 relative overflow-visible bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Centered Heading */}
        <div className="text-center max-w-[1000px] mx-auto mb-12">
          <h2 className="text-4xl md:text-[56px] font-extrabold text-white tracking-tight leading-tight text-center">
            <span className="relative inline-block mr-3">
              <span className={`relative z-10 text-white ${caveat.className} text-5xl md:text-7xl font-bold`}>
                {section.titleHighlight}
              </span>
              {/* Premium orange hand-drawn dashed scribble highlight under "Streamlined" */}
              <svg className="absolute -bottom-3 left-0 w-full h-4 text-[#f1833b] overflow-visible opacity-95" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path
                  d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8 C 25 6, 65 5, 95 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeDasharray="4 3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="text-white block lg:inline">
              {section.titleRest}
            </span>
          </h2>
        </div>

        {/* Subtext Paragraph */}
        <div className="text-center max-w-[900px] mx-auto mb-16">
          {section.paragraphs?.map((para, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <p
                className="text-gray-300 leading-relaxed font-normal text-xl"
                dangerouslySetInnerHTML={{ __html: para }}
              />
            </motion.div>
          ))}
        </div>

        {/* Circular Card layout matching Odoo's exact markup - fully responsive in a single block */}
        <div className="relative py-12 flex justify-center items-center">

          {/* Centered background circle matching x_wd_shape_circle_xs */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[460px] lg:w-[580px] h-[280px] sm:h-[460px] lg:h-[580px] bg-white/5 rounded-full -z-10 pointer-events-none select-none" />

          {/* Symmetrical Columns Row */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-12 max-w-[860px] w-full mx-auto">

            {/* Left Column: Card 1 (top) and Card 4 (bottom) */}
            <div className="w-[85%] sm:w-[70%] md:w-[46%] md:max-w-[360px] flex flex-col gap-6 md:gap-8 select-none">
              
              {/* Card 1: All RFQs */}
              <motion.div
                initial={{ opacity: 0, x: -40, y: -20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <Image
                  src={section.images.rfq1}
                  width={360}
                  height={250}
                  className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-xl w-full mx-auto object-contain hover:scale-[1.03] transition-transform duration-300"
                  alt="All RFQs and My RFQs chart"
                />
              </motion.div>

              {/* Card 4: New Replenishment Locations */}
              <motion.div
                initial={{ opacity: 0, x: -40, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="relative"
              >
                <Image
                  src={section.images.rfq4}
                  width={360}
                  height={250}
                  className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-xl w-full mx-auto object-contain hover:scale-[1.03] transition-transform duration-300"
                  alt="New replenishment locations chart"
                />
              </motion.div>

            </div>

            {/* Right Column: Card 2 (top) and Card 3 (bottom) */}
            <div className="w-[85%] sm:w-[70%] md:w-[46%] md:max-w-[360px] flex flex-col gap-6 md:gap-8 select-none">

              {/* Card 2: Total Status table */}
              <motion.div
                initial={{ opacity: 0, x: 40, y: -20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="relative"
              >
                <Image
                  src={section.images.rfq2}
                  width={360}
                  height={250}
                  className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-xl w-full mx-auto object-contain hover:scale-[1.03] transition-transform duration-300"
                  alt="RFQ Total and Status table"
                />
              </motion.div>

              {/* Card 3: Order Once / Automate actions list */}
              <motion.div
                initial={{ opacity: 0, x: 40, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="relative"
              >
                <Image
                  src={section.images.rfq3}
                  width={360}
                  height={250}
                  className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-xl w-full mx-auto object-contain hover:scale-[1.03] transition-transform duration-300"
                  alt="Automated replenishment actions dashboard"
                />
              </motion.div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
}

export function FossilSeamlessInventory({ section }) {
  return (
    <section className="py-20 relative overflow-visible bg-transparent w-full font-sans">
      
      {/* Background shape behind the content matching Odoo's x_wd_shape_short */}
      <div className="absolute right-0 top-[20%] w-[320px] sm:w-[480px] lg:w-[580px] h-[360px] sm:h-[500px] lg:h-[620px] bg-white/[0.02] rounded-l-[180px] sm:rounded-l-[240px] -z-10 pointer-events-none select-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1550px] relative z-10">

        {/* Heading & Subtext (Left aligned on desktop, centered on mobile matching screenshot) */}
        <div className="text-left max-w-[1250px] mx-auto mb-16">
          <h2 className="text-4xl md:text-[56px] font-extrabold text-white tracking-tight leading-tight mb-6">
            <span className="relative inline-block mr-3">
              <span className={`relative z-10 text-white ${caveat.className} text-5xl md:text-7xl font-bold`}>
                {section.titleHighlight}
              </span>
              {/* Odoo Signature Orange scribble underline */}
              <svg className="absolute -bottom-3 left-0 w-full h-4 text-[#f1833b] overflow-visible opacity-95" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path
                  d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8 C 25 6, 65 5, 95 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="text-white">
              {section.titleRest}
            </span>
          </h2>

          <div className="">
            {section.paragraphs?.map((para, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p
                  className="text-gray-300 leading-relaxed font-normal max-w-[760px] text-xl"
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Symmetrical Two-Column mockup layout */}
        <div className="flex flex-col lg:flex-row justify-center items-center gap-8 lg:gap-14 max-w-[1500px] w-full mx-auto relative mt-8">

          {/* Left Column: Large Inventory Dashboard Mockup (Col-lg-8 scale) */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[73%] select-none"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 relative">
              <Image
                src={section.images.inventory}
                width={900}
                height={600}
                className="w-full h-auto rounded-[8px] object-contain"
                alt="Odoo Inventory overview dashboard"
              />
            </div>
          </motion.div>

          {/* Right Column: Products List Mockup & Unbox Icon doodle (Col-lg-4 scale) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-[85%] sm:w-[70%] md:w-[50%] lg:w-[25%] flex flex-col justify-center relative select-none mt-4 lg:mt-0 pb-10"
          >
            {/* Products Image card wrapper */}
            <div className="p-1.5 bg-white border border-gray-200 rounded-[12px] shadow-2xl hover:scale-[1.02] transition-transform duration-300 relative">
              <Image
                src={section.images.products}
                width={320}
                height={500}
                className="w-full h-auto rounded-[8px] object-contain"
                alt="Fossil fuel products list panel"
              />
            </div>

            {/* Orange Unbox Icon doodle placed absolutely over the bottom-right corner of the panel */}
            <div className="absolute bottom-[-24px] right-[-24px] md:bottom-[-35px] md:right-[-35px] z-20 w-[100px] md:w-[130px] h-[130px]">
              <motion.div
                initial={{ scale: 0, rotate: 0 }}
                whileInView={{ scale: 1, rotate: 10 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
                className="w-full h-full relative"
              >
                <Image
                  src={section.images.unboxIcon}
                  fill
                  className="select-none pointer-events-none filter drop-shadow-lg object-contain"
                  style={{ filter: "hue-rotate(-110deg) saturate(1.8) brightness(1.1) drop-shadow(0 10px 15px rgba(241, 131, 59, 0.3))" }}
                  alt=""
                />
              </motion.div>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function FossilRefinedAccounting({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans">

      {/* Subtle right-side background shape */}
      <div className="absolute right-0 top-[10%] w-[300px] sm:w-[500px] lg:w-[640px] h-[400px] sm:h-[600px] lg:h-[700px] bg-white/[0.02] rounded-l-[200px] sm:rounded-l-[280px] -z-10 pointer-events-none select-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Top Row: Heading Left + Stat Right */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl md:text-[56px] font-extrabold text-white tracking-tight leading-tight">
              <span className="relative inline-block mr-3">
                <span className={`relative z-10 text-white ${caveat.className} text-5xl md:text-7xl font-bold`}>
                  {section.titleHighlight}
                </span>
                {/* Blue wavy scribble underline matching x_wd_blue_highlight_02 */}
                <svg className="absolute -bottom-3 left-0 w-full h-4 overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path
                    d="M 2 7 C 20 3, 50 2, 80 5 C 60 8, 30 9, 5 7 C 30 5, 65 4, 95 6"
                    stroke="#f1833b"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="text-white">{section.titleRest}</span>
            </h2>
          </motion.div>

          {/* Stat: 28,000 banks supported + bank icon */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center gap-4 lg:justify-end"
          >
            <div className="text-right">
              <span className={`block text-4xl md:text-5xl font-extrabold text-white ${caveat.className}`}>
                {section.statNumber}
              </span>
              <span className="block text-base md:text-xl text-gray-300 font-medium mt-1">
                {section.statLabel}
              </span>
            </div>
            <div className="relative w-16 md:w-20 h-20">
              <Image
                src={section.bankIcon}
                alt="Bank icon"
                fill
                className="select-none object-contain"
                style={{ filter: "hue-rotate(-185deg) saturate(2) brightness(1.1) drop-shadow(0 4px 12px rgba(241, 131, 59, 0.4))" }}
              />
            </div>
          </motion.div>

        </div>

        {/* Bottom Row: Paragraphs Left + Video Right */}
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">

          {/* Left: Three feature paragraphs */}
          <div className="w-full lg:w-[38%] flex flex-col gap-6">
            {section.paragraphs?.map((para, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="text-gray-300 leading-relaxed text-xl"
                dangerouslySetInnerHTML={{ __html: para }}
              />
            ))}
          </div>

          {/* Right: Accounting Video */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="w-full lg:w-[58%] select-none"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[14px] shadow-2xl hover:scale-[1.01] transition-transform duration-300">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto rounded-[10px] object-cover"
              >
                <source src={section.videoWebm} type="video/webm" />
                <source src={section.videoMp4} type="video/mp4" />
              </video>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
